const IGNORED_QUERY_KEYS = new Set(["hydrated"])

const normalizeValue = value => {
	if (value === undefined || value === null) {
		return null
	}
	if (typeof value === "boolean") {
		return value ? "true" : null
	}

	const stringValue = `${value}`.trim()

	if (!stringValue || stringValue === "undefined" || stringValue === "false") {
		return null
	}

	return stringValue
}

/**
 * Convert an object into a query string.
 * Skips ignored keys, falsy primitives and empty values by default.
 */
export const buildURLQuery = params => {
	if (!params || typeof params !== "object") {
		return ""
	}

	const parts = []

	Object.entries(params).forEach(([key, value]) => {
		if (!key || IGNORED_QUERY_KEYS.has(key)) {
			return
		}

		if (Array.isArray(value)) {
			value.forEach(item => {
				const normalized = normalizeValue(item)
				if (normalized !== null) {
					parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(normalized)}`)
				}
			})
			return
		}

		const normalized = normalizeValue(value)
		if (normalized !== null) {
			parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(normalized)}`)
		}
	})

	return parts.join("&")
}

const safeDecode = value => {
	try {
		return decodeURIComponent(value.replace(/\+/g, " "))
	} catch (error) {
		return value
	}
}

/**
 * Parse a query string into an object.
 */
export const queryParser = (queryStr = "") => {
	if (!queryStr || typeof queryStr !== "string") {
		return {}
	}

	const trimmed = queryStr.startsWith("?") ? queryStr.slice(1) : queryStr
	if (!trimmed) {
		return {}
	}

	return trimmed.split("&").reduce((accumulator, chunk) => {
		if (!chunk) {
			return accumulator
		}

		const [rawKey, rawValue = ""] = chunk.split("=")

		if (!rawKey) {
			return accumulator
		}

		const key = safeDecode(rawKey)

		if (!key || IGNORED_QUERY_KEYS.has(key)) {
			return accumulator
		}

		const value = safeDecode(rawValue)

		if (Object.prototype.hasOwnProperty.call(accumulator, key)) {
			const existing = accumulator[key]
			if (Array.isArray(existing)) {
				existing.push(value)
			} else {
				accumulator[key] = [existing, value]
			}
		} else {
			accumulator[key] = value
		}

		return accumulator
	}, {})
}

/**
 * Smoothly scroll the window to the top, if available.
 */
export const scrollToTop = () => {
	if (typeof window !== "undefined") {
		window.scrollTo({
			top: 0,
			behavior: "smooth",
		})
	}
}
